import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

import useHoverIntent from "hooks/useHoverIntent";

import BrandsDropdown from "./BrandsDropdown";
import {
  NAV_ITEM_CLASS,
  NAV_MENU_GROUP,
  PROMOTION_NAV_ITEMS,
} from "./navigationItems";
import ProductsDropdown from "./ProductsDropdown";

import { Icon } from "@/design-system";

const navLinkClass = `${NAV_ITEM_CLASS} font-medium text-ink-2 hover:text-ink`;

const dropdownItemClass =
  "flex w-full items-center gap-[var(--space-3)] rounded-sm px-[var(--space-3)] py-[10px] text-left font-sans text-sm text-ink [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

function HoverNavMenu({
  label,
  path,
  icon,
  items,
  accent = false,
  className = "",
}) {
  // 200 is the hook's default, spelled out because the group is the third argument.
  const { open, onMouseEnter, onMouseLeave, close } = useHoverIntent(
    200,
    undefined,
    NAV_MENU_GROUP
  );

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onMouseEnter}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <Link
        to={path}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${navLinkClass} font-semibold ${
          accent ? "text-orange-600 hover:text-orange-700" : ""
        }`}
      >
        {icon && <Icon name={icon} size={17} weight={400} fill={accent} />}
        {label}
        <Icon
          name="expand_more"
          size={17}
          weight={400}
          className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
            open ? "rotate-180" : ""
          }`}
        />
      </Link>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%_+_6px)] z-[var(--z-dropdown)] w-[268px] rounded-lg border border-line-subtle bg-card px-[var(--space-2)] py-[var(--space-2)] shadow-lg"
        >
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              role="menuitem"
              onClick={close}
              className={dropdownItemClass}
            >
              <Icon
                name={item.icon}
                size={20}
                weight={400}
                className="text-ink-2"
              />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DesktopNav({ classification }) {
  const { isAuthenticated } = useAuth0();

  return (
    <nav
      className="hidden border-t border-line-subtle bg-card lg:block"
      aria-label="Primary navigation"
    >
      <div className="mx-auto flex max-w-container items-center gap-[var(--space-1)] px-[var(--gutter)] py-0">
        <Link to="/" className={`${navLinkClass} font-semibold`}>
          <Icon name="home" size={18} weight={400} />
          Home
        </Link>

        <ProductsDropdown classification={classification} />

        <BrandsDropdown />

        <Link to="/partners" className={navLinkClass}>
          Partners
        </Link>

        {isAuthenticated && (
          <>
            <Link to="/favorites" className={navLinkClass}>
              Saved lists
            </Link>
            <Link to="/re-order" className={navLinkClass}>
              Order again
            </Link>
          </>
        )}

        <HoverNavMenu
          label="Promotions & Catalogues"
          path="/promotions"
          icon="sell"
          items={PROMOTION_NAV_ITEMS}
          accent
          className="ml-auto"
        />
      </div>
    </nav>
  );
}
